import { FaCalculator, FaPlus, FaSearch, FaSpinner, FaTrash } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

const Api6Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const entries = state.entries || [];
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const refrigerantGases = ['R-410A', 'R-134a', 'R-404A', 'R-32', 'R-407C', 'CO₂ (R-744)', 'Ammonia (R-717)', 'Other'];
    const equipmentTypes = ['Chiller', 'Packaged AC', 'Split', 'VRF', 'Cold room', 'Vehicle AC', 'Process chiller', 'Other'];
    const maintenanceReasons = ['Leak repair', 'Top-up', 'Commissioning', 'Other'];

    const refrigerantSearchConfig = {
        'R-410A': { query: 'R-410A refrigerant', preferred_unit_type: 'Weight' },
        'R-134a': { query: 'R-134a refrigerant', preferred_unit_type: 'Weight' },
        'R-404A': { query: 'R-404A refrigerant', preferred_unit_type: 'Weight' },
        'R-32': { query: 'R-32 refrigerant', preferred_unit_type: 'Weight' },
        'R-407C': { query: 'R-407C refrigerant', preferred_unit_type: 'Weight' },
        'CO₂ (R-744)': { query: 'CO2 refrigerant R-744', preferred_unit_type: 'Weight' },
        'Ammonia (R-717)': { query: 'ammonia refrigerant R-717', preferred_unit_type: 'Weight' },
        'Other': { query: 'refrigerant gas', preferred_unit_type: 'Weight' }
    };

    const defaultEmissionFactors = {
        'R-410A': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_r410a", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'R-134a': { activity_id: "fugitive_gas-type_hfc_134a", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'R-404A': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_r404a", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'R-32': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_r32", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'R-407C': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_r407c", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'CO₂ (R-744)': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_co2", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'Ammonia (R-717)': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_ammonia", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" },
        'Other': { activity_id: "fugitive_emissions-type_refrigerants-refrigerant_na", source: "IPCC", region: "GLOBAL", year: 2021, unit: "kg", unit_type: "Weight" }
    };

    const searchEmissionFactors = async (gas) => {
        try {
            const config = refrigerantSearchConfig[gas];
            if (!config) return defaultEmissionFactors[gas];

            const params = new URLSearchParams({
                query: config.query,
                data_version: '^3',
                region: 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '5'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return defaultEmissionFactors[gas];
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const refrigerantFactors = data.results.filter(factor =>
                    factor.activity_id.includes('refrigerant') ||
                    factor.activity_id.includes('fugitive') ||
                    factor.category === 'Refrigerants' ||
                    factor.category === 'Fugitive Emissions'
                );

                let bestFactor = refrigerantFactors[0] || data.results[0];

                if (config.preferred_unit_type) {
                    const preferredFactor = refrigerantFactors.find(f => f.unit_type === config.preferred_unit_type)
                        || data.results.find(f => f.unit_type === config.preferred_unit_type);
                    if (preferredFactor) bestFactor = preferredFactor;
                }

                updateState({ availableFactors: { ...state.availableFactors, [gas]: bestFactor } });
                return bestFactor;
            }

            return defaultEmissionFactors[gas];
        } catch (error) {
            console.error(`Error searching emission factors for ${gas}:`, error);
            return defaultEmissionFactors[gas];
        }
    };

    const handleSearchFactors = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one refrigerant entry first');
            return;
        }

        const gasesToSearch = [...new Set(entries.map(entry => entry.gas).filter(Boolean))];
        if (gasesToSearch.length === 0) {
            toastr.error('Please add entries with refrigerant gases first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Searching for best emission factors...');

        try {
            let foundCount = 0;
            for (const gas of gasesToSearch) {
                const factor = await searchEmissionFactors(gas);
                if (factor && factor !== defaultEmissionFactors[gas]) {
                    foundCount++;
                }
                // Always store the factor (whether found via API or using default) in availableFactors
                updateState({
                    availableFactors: {
                        ...state.availableFactors,
                        [gas]: factor || defaultEmissionFactors[gas]
                    }
                });
            }

            // Always show success message, regardless of whether factors were found via API or using defaults
            toastr.success(`Emission factors ready for ${gasesToSearch.length} refrigerant(s)! Click Calculate to proceed.`);
        } catch (error) {
            // Even if search fails, we have default factors, so show success
            toastr.success(`Emission factors ready for ${gasesToSearch.length} refrigerant(s)! Click Calculate to proceed.`);
        } finally {
            updateState({ isSearching: false });
        }
    };

    const callClimateIQAPI = async (entry, index) => {
        try {
            const emissionFactor = state.availableFactors?.[entry.gas] || defaultEmissionFactors[entry.gas];
            if (!emissionFactor) throw new Error(`No emission factor found for ${entry.gas}`);

            let quantity = parseFloat(entry.quantityKg) || 0;

            if (quantity <= 0 || isNaN(quantity)) {
                throw new Error(`Invalid quantity for ${entry.gas}. Please provide quantity in kilograms.`);
            }

            // Convert kg to tons for API (divide by 1000)
            const quantityInTons = quantity / 1000;

            let requestBody;

            // Use exact payload formats from documentation for specific gases
            if (entry.gas === 'R-134a') {
                requestBody = {
                    emission_factor: {
                        activity_id: "fugitive_gas-type_hfc_134a",
                        source: "GHG Protocol",
                        region: "GLOBAL",
                        year: 2021,
                        source_lca_activity: "unknown",
                        data_version: "^0",
                        allowed_data_quality_flags: [
                            "notable_methodological_variance"
                        ]
                    },
                    parameters: {
                        weight: quantityInTons,
                        weight_unit: "t"
                    }
                };
            } else if (entry.gas === 'R-32') {
                requestBody = {
                    emission_factor: {
                        activity_id: "fugitive_gas-type_r32",
                        data_version: "^0"
                    },
                    parameters: {
                        weight: quantityInTons,
                        weight_unit: "t"
                    }
                };
            } else if (entry.gas === 'CO₂ (R-744)') {
                requestBody = {
                    emission_factor: {
                        activity_id: "fugitive_gas-type_r_744",
                        source: "GLEC",
                        region: "GLOBAL",
                        year: 2019,
                        source_lca_activity: "unknown",
                        data_version: "^0"
                    },
                    parameters: {
                        weight: quantityInTons,
                        weight_unit: "t"
                    }
                };
            } else if (entry.gas === 'Ammonia (R-717)') {
                requestBody = {
                    emission_factor: {
                        activity_id: "fugitive_gas-type_r_717",
                        data_version: "^0"
                    },
                    parameters: {
                        weight: quantityInTons,
                        weight_unit: "t"
                    }
                };
            } else {
                // For other gases, use the existing logic with kg
                requestBody = {
                    emission_factor: {
                        activity_id: emissionFactor.activity_id,
                        source: emissionFactor.source,
                        region: emissionFactor.region,
                        year: parseInt(emissionFactor.year),
                        data_version: "^3"
                    },
                    parameters: {
                        weight: quantity,
                        weight_unit: "kg"
                    }
                };
            }

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${entry.gas} entry ${index}:`, error);
            throw error;
        }
    };

    const handleCalculate = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one refrigerant entry');
            return;
        }

        for (const [index, entry] of entries.entries()) {
            if (!entry.gas) {
                toastr.error(`Entry ${index + 1}: Please select a refrigerant gas`);
                return;
            }
            if (!entry.quantityKg) {
                toastr.error(`Entry ${index + 1}: Please provide quantity in kilograms`);
                return;
            }
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions via API...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];

                try {
                    const apiResult = await callClimateIQAPI(entry, i);
                    const resultKey = `entry_${i}`;

                    results[resultKey] = {
                        gas: entry.gas,
                        quantity: parseFloat(entry.quantityKg),
                        unit: 'kg',
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: apiResult.co2e_calculation_method,
                        activity_data: apiResult.activity_data,
                        emission_factor: state.availableFactors?.[entry.gas] || defaultEmissionFactors[entry.gas]
                    };

                    toastr.success(`Calculated emissions for ${entry.gas}: ${apiResult.co2e.toFixed(2)} ${apiResult.co2e_unit}`);
                    hasSuccessfulCalculation = true;
                } catch (error) {
                    console.error(`API error for entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate emissions for entry ${i + 1}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO2e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs and API key.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const handleAddEntry = () => {
        const newEntries = [...entries, {
            reportingPeriod: '',
            gas: '',
            quantityKg: '',
            equipmentType: '',
            maintenanceReason: '',
            evidence: null
        }];
        updateState({ entries: newEntries });
    };

    const handleEntryChange = (index, field, value) => {
        const updated = [...entries];
        updated[index][field] = value;
        updateState({ entries: updated });
    };

    const handleRemoveEntry = (index) => {
        updateState({ entries: entries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        updateState({
            formData: { ...formData, year: '2025' },
            entries: [{
                reportingPeriod: '2025 (Jan-Dec)',
                gas: 'R-410A',
                quantityKg: '15',
                equipmentType: 'Packaged AC',
                maintenanceReason: 'Leak repair',
                evidence: null
            }]
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            Please tell us, for each refrigerant gas you used, how many kilograms were added to equipment during the year
                        </label>
                        <p className="text-sm text-gray-600 mb-6">
                            This information is often available in HVAC/R maintenance logs, F-gas registers, contractor work orders, purchase records and leak repair reports.
                        </p>

                        <div className="mb-6">
                            <button
                                onClick={handleSearchFactors}
                                disabled={state.isSearching}
                                className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {state.isSearching ? (
                                    <FaSpinner className="w-4 h-4 animate-spin" />
                                ) : (
                                    <FaSearch className="w-4 h-4" />
                                )}
                                <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                            </button>
                        </div>

                        {Object.keys(state.availableFactors || {}).length > 0 && (
                            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                                <h3 className="font-semibold text-green-800 mb-2">Available Emission Factors</h3>
                                <div className="space-y-2">
                                    {Object.entries(state.availableFactors).map(([gas, factor]) => (
                                        <div key={gas} className="text-sm text-green-700">
                                            <strong>{gas}:</strong> {factor.name} ({factor.source}, {factor.region})
                                            <br />
                                            <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={handleAddEntry}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 mb-6"
                        >
                            <FaPlus className="w-4 h-4" />
                            <span>Add Refrigerant Entry</span>
                        </button>

                        {entries.map((entry, index) => {
                            const resultKey = `entry_${index}`;
                            const result = state.calculationResults?.[resultKey];

                            return (
                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-semibold">Entry {index + 1}</h4>
                                        <button
                                            onClick={() => handleRemoveEntry(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {result && (
                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                            <p className="text-sm text-green-800 font-medium">
                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                            </p>
                                            <p className="text-xs text-green-600">
                                                Based on {result.quantity.toFixed(2)} {result.unit} of {entry.gas}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., 2024 (Jan–Dec)"
                                                value={entry.reportingPeriod}
                                                onChange={(e) => handleEntryChange(index, 'reportingPeriod', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Gas *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.gas}
                                                onChange={(e) => handleEntryChange(index, 'gas', e.target.value)}
                                            >
                                                <option value="">Select gas</option>
                                                {refrigerantGases.map(gas => <option key={gas} value={gas}>{gas}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Added (kg) *</label>
                                            <input
                                                type="number"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="Enter kilograms"
                                                value={entry.quantityKg}
                                                onChange={(e) => handleEntryChange(index, 'quantityKg', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Type</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.equipmentType}
                                                onChange={(e) => handleEntryChange(index, 'equipmentType', e.target.value)}
                                            >
                                                <option value="">Select type</option>
                                                {equipmentTypes.map(type => <option key={type} value={type}>{type}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance Reason</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.maintenanceReason}
                                                onChange={(e) => handleEntryChange(index, 'maintenanceReason', e.target.value)}
                                            >
                                                <option value="">Select reason</option>
                                                {maintenanceReasons.map(reason => <option key={reason} value={reason}>{reason}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                            <input
                                                type="file"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => handleEntryChange(index, 'evidence', e.target.files[0])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {Object.keys(state.calculationResults || {}).length > 0 && (
                            <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results</h3>
                                <div className="space-y-2">
                                    {Object.entries(state.calculationResults).map(([key, result]) => (
                                        <div key={key} className="flex justify-between items-center">
                                            <span className="text-gray-700">{result.gas}</span>
                                            <span className="font-semibold text-green-700">
                                                {result.co2e.toFixed(2)} {result.co2e_unit}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="border-t border-gray-300 pt-2 mt-2">
                                        <div className="flex justify-between items-center font-bold text-lg">
                                            <span>Total Emissions:</span>
                                            <span className="text-blue-700">
                                                {Object.values(state.calculationResults).reduce((sum, result) => sum + (result.co2e || 0), 0).toFixed(2)} kg CO2e
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Api6Form;